import { useState, useRef, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import { getRegisterFields, saveRegisterFields, saveSmsConfig, RegisterField as ApiRegisterField, getSiteSettings, saveSiteSettings, getSmsConfig, getSinglePages, updateSinglePage, SinglePage, getVipPlans, saveVipPlans, deleteVipPlan } from "@/utils/adminApi";
import { uploadImage } from "@/utils/api";

// 简单的消息提示
const toast = (msg: string, type: 'success' | 'error' = 'success') => {
  const toastEl = document.createElement('div');
  toastEl.style.cssText = `
    position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
    padding: 12px 24px; border-radius: 8px; z-index: 99999;
    background: ${type === 'success' ? '#22c55e' : '#ef4444'};
    color: white; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  `;
  toastEl.textContent = msg;
  document.body.appendChild(toastEl);
  setTimeout(() => toastEl.remove(), 3000);
};

type TabKey = "basic" | "logo" | "keywords" | "contact" | "agreement" | "footer-menus" | "search" | "banner" | "features" | "vip" | "register" | "ssl";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "basic", label: "基本信息", icon: "ri-information-line" },
  { key: "logo", label: "Logo设置", icon: "ri-image-line" },
  { key: "keywords", label: "关键词SEO", icon: "ri-search-line" },
  { key: "contact", label: "联系方式", icon: "ri-phone-line" },
  { key: "footer-menus", label: "底部菜单", icon: "ri-layout-bottom-line" },
  { key: "search", label: "搜索设置", icon: "ri-search-eye-line" },
  { key: "banner", label: "轮播图设置", icon: "ri-slideshow-line" },
  { key: "agreement", label: "协议设置", icon: "ri-file-text-line" },
  { key: "features", label: "功能配置", icon: "ri-toggle-line" },
  { key: "vip", label: "VIP设置", icon: "ri-vip-crown-line" },
  { key: "register", label: "注册设置", icon: "ri-user-add-line" },
  { key: "ssl", label: "SSL配置", icon: "ri-shield-check-line" },
];

function SaveBar({ onSave, saved, disabled }: { onSave: () => void; saved: boolean; disabled?: boolean }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <button
        onClick={onSave}
        disabled={disabled}
        className="px-6 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
      >
        {disabled ? "保存中..." : "保存设置"}
      </button>
      {saved && !disabled && (
        <span className="text-sm text-green-600 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-line" />
          保存成功
        </span>
      )}
    </div>
  );
}

function ToggleSwitch({ value, onChange, label, desc }: { value: boolean; onChange: (v: boolean) => void; label: string; desc: string }) {
  return (
    <div className="flex items-center justify-between py-3.5 border-b border-gray-50 last:border-0">
      <div>
        <div className="text-sm font-medium text-gray-800">{label}</div>
        <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${value ? "bg-orange-500" : "bg-gray-200"}`}
      >
        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? "translate-x-4" : "translate-x-0"}`} />
      </button>
    </div>
  );
}

// ---- Tab: Basic ----
function BasicTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    siteName: "",
    siteDesc: "",
    icp: "",
    icpUrl: "",
    footerInfo: "",
  });
  const [saved, setSaved] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // 加载设置
  useEffect(() => {
    getSiteSettings().then((settings) => {
      setForm({
        siteName: settings.site_name || "",
        siteDesc: settings.footer_info || "",
        icp: settings.icp || "",
        icpUrl: settings.icp_url || "https://beian.miit.gov.cn",
        footerInfo: settings.footer_info || "",
      });
    }).catch(() => {
      toast("加载设置失败", "error");
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  // 保存设置
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({
        site_name: form.siteName,
        footer_info: form.footerInfo,
        icp: form.icp,
        icp_url: form.icpUrl,
      } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (err) {
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-gray-600 mb-1.5">网站名称</label>
        <input value={form.siteName} onChange={(e) => set("siteName", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
      </div>
      <div>
        <label className="block text-sm text-gray-600 mb-1.5">网站描述</label>
        <textarea value={form.footerInfo} onChange={(e) => set("footerInfo", e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none" placeholder="显示在首页和页面底部，介绍您的网站" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">ICP备案号</label>
          <input value={form.icp} onChange={(e) => set("icp", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">备案链接</label>
          <input value={form.icpUrl} onChange={(e) => set("icpUrl", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
      </div>
      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />
    </div>
  );
}

// ---- Tab: Logo ----
function LogoTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [saved, setSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const faviconRef = useRef<HTMLInputElement>(null);

  // 加载设置
  useEffect(() => {
    getSiteSettings().then((settings) => {
      setLogoUrl(settings.site_logo || "");
      // favicon 可以从 site_logo 派生或单独存储
      setFaviconUrl(settings.site_logo || "");
    }).catch(() => {
      toast("加载设置失败", "error");
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  // 保存设置
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({
        site_logo: logoUrl,
      } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (err) {
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">网站 Logo</label>
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50 overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt="logo" className="w-full h-full object-contain p-2" />
            ) : (
              <i className="ri-image-add-line text-2xl text-gray-300" />
            )}
          </div>
          <div className="space-y-2">
            <button onClick={() => logoRef.current?.click()} disabled={logoUploading} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer disabled:opacity-50">
              {logoUploading ? "上传中..." : "上传 Logo"}
            </button>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setLogoUploading(true); try { const url = await uploadImage(f); setLogoUrl(url); } catch (err: any) { toast(err.message || '上传失败', 'error'); } finally { setLogoUploading(false); } }} />
            <div className="text-xs text-gray-400">建议尺寸 200×200px，支持 PNG/SVG</div>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs text-gray-500 mb-1">或直接输入图片链接</label>
          <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">网站 Favicon</label>
        <div className="flex items-center gap-5">
          <div className="w-12 h-12 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50 overflow-hidden">
            {faviconUrl ? (
              <img src={faviconUrl} alt="favicon" className="w-full h-full object-contain" />
            ) : (
              <i className="ri-image-line text-xl text-gray-300" />
            )}
          </div>
          <div className="space-y-2">
            <button onClick={() => faviconRef.current?.click()} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer">
              上传 Favicon
            </button>
            <input ref={faviconRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setFaviconUrl(URL.createObjectURL(f)); }} />
            <div className="text-xs text-gray-400">建议尺寸 32×32px，支持 ICO/PNG</div>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs text-gray-500 mb-1">或直接输入图片链接</label>
          <input value={faviconUrl} onChange={(e) => setFaviconUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
      </div>

      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />
    </div>
  );
}

// ---- Tab: Keywords ----
function KeywordsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [saved, setSaved] = useState(false);

  // 加载设置
  useEffect(() => {
    getSiteSettings().then((settings) => {
      setMetaTitle(settings.seo_title || "");
      setMetaDesc(settings.seo_description || "");
      // seo_keywords 是逗号分隔的字符串，需要解析为数组
      const kwStr = settings.seo_keywords || "";
      setKeywords(kwStr ? kwStr.split(",").map(k => k.trim()).filter(Boolean) : []);
    }).catch(() => {
      toast("加载设置失败", "error");
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  // 保存设置
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({
        seo_title: metaTitle,
        seo_description: metaDesc,
        seo_keywords: keywords.join(","),
      } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (err) {
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    const kw = input.trim();
    if (kw && !keywords.includes(kw)) {
      setKeywords((prev) => [...prev, kw]);
      setInput("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">SEO 标题（Meta Title）</label>
        <input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        <div className="text-xs text-gray-400 mt-1">建议 60 字符以内，当前 {metaTitle.length} 字符</div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">SEO 描述（Meta Description）</label>
        <textarea value={metaDesc} onChange={(e) => setMetaDesc(e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none" />
        <div className="text-xs text-gray-400 mt-1">建议 120-160 字符，当前 {metaDesc.length} 字符</div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">SEO 关键词</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {keywords.map((kw) => (
            <span key={kw} className="flex items-center gap-1.5 px-3 py-1 bg-orange-50 text-orange-600 text-sm rounded-full">
              {kw}
              <button onClick={() => setKeywords((prev) => prev.filter((k) => k !== kw))} className="w-4 h-4 flex items-center justify-center hover:text-red-500 cursor-pointer">
                <i className="ri-close-line text-xs" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addKeyword()}
            placeholder="输入关键词，回车添加"
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
          />
          <button onClick={addKeyword} className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
            添加
          </button>
        </div>
      </div>
      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />
    </div>
  );
}

// ---- Tab: Contact ----
function ContactTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    companyName: "",
    phone: "",
    email: "",
    wechat: "",
    qq: "",
    address: "",
    workHours: "",
  });
  const [saved, setSaved] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // 加载设置
  useEffect(() => {
    getSiteSettings().then((settings) => {
      const contact = settings.contact_info || {};
      setForm({
        companyName: contact.company_name || "",
        phone: contact.phone || "",
        email: contact.email || "",
        wechat: contact.wechat || "",
        qq: contact.qq || "",
        address: contact.address || "",
        workHours: contact.work_hours || "",
      });
    }).catch(() => {
      toast("加载设置失败", "error");
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  // 保存设置
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({
        contact_info: {
          company_name: form.companyName,
          phone: form.phone,
          email: form.email,
          wechat: form.wechat,
          qq: form.qq,
          address: form.address,
          work_hours: form.workHours,
        },
      } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (err) {
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-gray-600 mb-1.5">公司名称</label>
        <input value={form.companyName} onChange={(e) => set("companyName", e.target.value)} placeholder="请输入公司名称" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">联系电话</label>
          <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">联系邮箱</label>
          <input value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">微信号</label>
          <input value={form.wechat} onChange={(e) => set("wechat", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">QQ号</label>
          <input value={form.qq} onChange={(e) => set("qq", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-600 mb-1.5">公司地址</label>
        <input value={form.address} onChange={(e) => set("address", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
      </div>
      <div>
        <label className="block text-sm text-gray-600 mb-1.5">工作时间</label>
        <input value={form.workHours} onChange={(e) => set("workHours", e.target.value)} placeholder="如：周一至周五 09:00-18:00" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
      </div>
      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />
    </div>
  );
}

// ---- Tab: Footer Menus ----
interface FooterMenuItemForm {
  id: string;
  label: string;
  href: string;
  external: boolean;
  enabled: boolean;
}

const defaultFooterMenus: FooterMenuItemForm[] = [
  { id: "fm1", label: "首页", href: "/", external: false, enabled: true },
  { id: "fm2", label: "发布信息", href: "/publish", external: false, enabled: true },
  { id: "fm3", label: "开通VIP", href: "/vip", external: false, enabled: true },
  { id: "fm4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
  { id: "fm5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
  { id: "fm6", label: "关于我们", href: "/page/about", external: false, enabled: true },
];

function FooterMenusTab() {
  const [menus, setMenus] = useState<FooterMenuItemForm[]>(defaultFooterMenus);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editMenu, setEditMenu] = useState<FooterMenuItemForm | null>(null);
  const [menuForm, setMenuForm] = useState<FooterMenuItemForm>({ id: "", label: "", href: "", external: false, enabled: true });

  // 加载数据
  useEffect(() => {
    getSiteSettings().then((settings) => {
      if (settings.footer_menus && settings.footer_menus.length > 0) {
        setMenus(settings.footer_menus);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  // 保存到API
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({ footer_menus: menus });
      toast("保存成功");
    } catch (e) {
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAddMenu = () => {
    const newItem: FooterMenuItemForm = { id: "fm-" + Date.now(), label: "", href: "", external: false, enabled: true };
    setMenuForm(newItem);
    setEditMenu(newItem);
  };

  const openEditMenu = (item: FooterMenuItemForm) => {
    setMenuForm({ ...item });
    setEditMenu(item);
  };

  const saveMenu = () => {
    if (!menuForm.label.trim() || !menuForm.href.trim()) return;
    setMenus((prev) => {
      const idx = prev.findIndex((m) => m.id === menuForm.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = menuForm; return next; }
      return [...prev, menuForm];
    });
    setEditMenu(null);
  };

  const deleteMenu = (id: string) => setMenus((prev) => prev.filter((m) => m.id !== id));
  const toggleMenu = (id: string) => setMenus((prev) => prev.map((m) => m.id === id ? { ...m, enabled: !m.enabled } : m));
  const moveMenu = (idx: number, dir: -1 | 1) => {
    const next = [...menus];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setMenus(next);
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-400">加载中...</div>;
  }

  return (
    <div className="space-y-7">
      {/* Footer Nav Menus */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">底部导航菜单</h3>
            <p className="text-xs text-gray-400 mt-0.5">显示在页脚中间横排区域，支持内部链接和外部链接</p>
          </div>
          <button
            onClick={openAddMenu}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            新增菜单
          </button>
        </div>

        <div className="space-y-2">
          {menus.map((item, idx) => (
            <div key={item.id} className={`flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-100 ${!item.enabled ? "opacity-50" : ""}`}>
              <div className="flex flex-col gap-0.5 shrink-0">
                <button onClick={() => moveMenu(idx, -1)} disabled={idx === 0} className="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
                  <i className="ri-arrow-up-s-line text-xs" />
                </button>
                <button onClick={() => moveMenu(idx, 1)} disabled={idx === menus.length - 1} className="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
                  <i className="ri-arrow-down-s-line text-xs" />
                </button>
              </div>
              <span className="text-xs text-gray-300 w-4 text-center shrink-0">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  {item.external && (
                    <span className="text-xs bg-sky-50 text-sky-500 px-1.5 py-0.5 rounded">外部</span>
                  )}
                  {!item.enabled && (
                    <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">隐藏</span>
                  )}
                </div>
                <span className="text-xs text-gray-400 font-mono truncate block">{item.href}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggleMenu(item.id)}
                  className={`relative w-8 h-5 rounded-full transition-colors cursor-pointer ${item.enabled ? "bg-orange-500" : "bg-gray-200"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${item.enabled ? "translate-x-3" : "translate-x-0"}`} />
                </button>
                <button onClick={() => openEditMenu(item)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer">编辑</button>
                <button onClick={() => deleteMenu(item.id)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer">删除</button>
              </div>
            </div>
          ))}
          {menus.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">暂无菜单，点击新增</div>
          )}
        </div>
      </div>

      <SaveBar onSave={handleSave} saved={saving} disabled={saving} />

      {/* Edit Menu Modal */}
      {editMenu && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditMenu(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">{menus.find(m => m.id === menuForm.id) ? "编辑菜单" : "新增菜单"}</h3>
              <button onClick={() => setEditMenu(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">菜单名称 <span className="text-red-400">*</span></label>
                <input
                  value={menuForm.label}
                  onChange={(e) => setMenuForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="如：帮助中心"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">链接地址 <span className="text-red-400">*</span></label>
                <input
                  value={menuForm.href}
                  onChange={(e) => setMenuForm((f) => ({ ...f, href: e.target.value }))}
                  placeholder="/path 或 https://..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs font-medium text-gray-700">外部链接</div>
                  <div className="text-xs text-gray-400 mt-0.5">开启后将在新窗口打开</div>
                </div>
                <button
                  onClick={() => setMenuForm((f) => ({ ...f, external: !f.external }))}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${menuForm.external ? "bg-orange-500" : "bg-gray-200"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${menuForm.external ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setEditMenu(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
              <button
                onClick={saveMenu}
                disabled={!menuForm.label.trim() || !menuForm.href.trim()}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer disabled:opacity-50"
              >保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Tab: Search Settings ----
function SearchTab() {
  const [searchEnabled, setSearchEnabled] = useState(true);
  const [searchDisabledMessage, setSearchDisabledMessage] = useState("搜索功能已关闭");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // 加载设置
  useEffect(() => {
    getSiteSettings().then((settings) => {
      setSearchEnabled(settings.search_enabled ?? true);
      setSearchDisabledMessage(settings.search_disabled_message || "搜索功能已关闭");
    }).catch(() => {
      toast("加载设置失败", "error");
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  // 保存设置
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({
        search_enabled: searchEnabled,
        search_disabled_message: searchDisabledMessage,
      } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (err) {
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Enable Toggle */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-800">开启网站搜索</div>
            <div className="text-xs text-gray-400 mt-0.5">关闭后，用户在前台搜索时将显示提示信息</div>
          </div>
          <button
            onClick={() => setSearchEnabled(!searchEnabled)}
            className={`relative w-11 h-7 rounded-full transition-colors cursor-pointer shrink-0 ${searchEnabled ? "bg-orange-500" : "bg-gray-200"}`}
          >
            <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${searchEnabled ? "translate-x-[18px]" : "translate-x-0"}`} />
          </button>
        </div>
      </div>

      {/* Disabled Message */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-gray-700">关闭搜索提示文案</label>
        </div>
        <textarea
          value={searchDisabledMessage}
          onChange={(e) => setSearchDisabledMessage(e.target.value)}
          rows={3}
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none"
          placeholder="当搜索功能关闭后，用户搜索时将显示此提示信息"
        />
        <div className="text-xs text-gray-400 mt-1">建议 50 字符以内，当前 {searchDisabledMessage.length} 字符</div>
      </div>

      {/* Preview */}
      <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
        <div className="flex items-start gap-2.5">
          <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-500 text-sm" />
          </div>
          <div className="text-xs text-amber-700 leading-relaxed">
            <strong>提示：</strong>关闭搜索功能后，前台首页的搜索框将显示此提示文案，用户无法进行搜索操作。如需完全禁用搜索，请同时关闭首页Banner区域的搜索框。
          </div>
        </div>
      </div>

      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />
    </div>
  );
}

// ---- Tab: Banner Settings ----
interface BannerItemForm {
  id: string;
  image: string;
  link: string;
  title: string;
  subtitle: string;
  enabled: boolean;
}

const defaultBanners: BannerItemForm[] = [
  { id: "bn1", image: "https://readdy.ai/api/search-image?query=abstract%20warm%20gradient%20background%20soft%20orange%20peach%20tones%20minimal%20modern%20business%20professional%20smooth%20texture&width=1920&height=800&seq=hero-bg&orientation=landscape", link: "/", title: "专业商业服务", subtitle: "信息平台", enabled: true },
  { id: "bn2", image: "https://readdy.ai/api/search-image?query=modern%20business%20technology%20background%20blue%20gradient%20professional&width=1920&height=800&seq=banner2&orientation=landscape", link: "/", title: "优质服务", subtitle: "触手可及", enabled: true },
];

function BannerTab() {
  const [banners, setBanners] = useState<BannerItemForm[]>(defaultBanners);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editBanner, setEditBanner] = useState<BannerItemForm | null>(null);
  const [bannerForm, setBannerForm] = useState<BannerItemForm>({ id: "", image: "", link: "/", title: "", subtitle: "", enabled: true });

  // 加载数据
  useEffect(() => {
    getSiteSettings().then((settings) => {
      if (settings.banner_settings && settings.banner_settings.length > 0) {
        setBanners(settings.banner_settings);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  // 保存
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({ banner_settings: banners });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (e) {
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAddBanner = () => {
    const newItem: BannerItemForm = { id: "bn-" + Date.now(), image: "", link: "/", title: "", subtitle: "", enabled: true };
    setBannerForm(newItem);
    setEditBanner(newItem);
  };

  const openEditBanner = (item: BannerItemForm) => {
    setBannerForm({ ...item });
    setEditBanner(item);
  };

  const saveBanner = () => {
    if (!bannerForm.title.trim()) return;
    setBanners((prev) => {
      const idx = prev.findIndex((b) => b.id === bannerForm.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = bannerForm; return next; }
      return [...prev, bannerForm];
    });
    setEditBanner(null);
  };

  const deleteBanner = (id: string) => setBanners((prev) => prev.filter((b) => b.id !== id));
  const toggleBanner = (id: string) => setBanners((prev) => prev.map((b) => b.id === id ? { ...b, enabled: !b.enabled } : b));
  const moveBanner = (idx: number, dir: -1 | 1) => {
    const next = [...banners];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setBanners(next);
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-400">加载中...</div>;
  }

  return (
    <div className="space-y-7">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">首页轮播图</h3>
            <p className="text-xs text-gray-400 mt-0.5">设置首页Banner轮播图片，支持设置链接和文字描述</p>
          </div>
          <button
            onClick={openAddBanner}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            新增轮播图
          </button>
        </div>

        <div className="space-y-3">
          {banners.map((item, idx) => (
            <div key={item.id} className={`flex items-center gap-3 px-3 py-3 bg-gray-50 rounded-xl border border-gray-100 ${!item.enabled ? "opacity-50" : ""}`}>
              <div className="flex flex-col gap-0.5 shrink-0">
                <button onClick={() => moveBanner(idx, -1)} disabled={idx === 0} className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
                  <i className="ri-arrow-up-s-line text-xs" />
                </button>
                <button onClick={() => moveBanner(idx, 1)} disabled={idx === banners.length - 1} className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
                  <i className="ri-arrow-down-s-line text-xs" />
                </button>
              </div>
              
              <div className="w-24 h-14 rounded-lg overflow-hidden bg-gray-200 shrink-0">
                {item.image ? (
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <i className="ri-image-line text-lg" />
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{item.title || "未设置标题"}</span>
                  {item.subtitle && <span className="text-xs text-orange-500">{item.subtitle}</span>}
                  {!item.enabled && <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">隐藏</span>}
                </div>
                <span className="text-xs text-gray-400 font-mono truncate block">{item.link}</span>
              </div>
              
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggleBanner(item.id)}
                  className={`relative w-8 h-5 rounded-full transition-colors cursor-pointer ${item.enabled ? "bg-orange-500" : "bg-gray-200"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${item.enabled ? "translate-x-3" : "translate-x-0"}`} />
                </button>
                <button onClick={() => openEditBanner(item)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer">编辑</button>
                <button onClick={() => deleteBanner(item.id)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer">删除</button>
              </div>
            </div>
          ))}
          {banners.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">暂无轮播图，点击新增</div>
          )}
        </div>
      </div>

      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />

      {/* Edit Banner Modal */}
      {editBanner && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditBanner(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">{banners.find(b => b.id === bannerForm.id) ? "编辑轮播图" : "新增轮播图"}</h3>
              <button onClick={() => setEditBanner(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">图片链接 <span className="text-red-400">*</span></label>
                <input
                  value={bannerForm.image}
                  onChange={(e) => setBannerForm((f) => ({ ...f, image: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                />
                <div className="text-xs text-gray-400 mt-1">建议尺寸 1920×600 像素</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">主标题</label>
                  <input
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="如：专业商业服务"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">副标题</label>
                  <input
                    value={bannerForm.subtitle}
                    onChange={(e) => setBannerForm((f) => ({ ...f, subtitle: e.target.value }))}
                    placeholder="如：信息平台"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">跳转链接</label>
                <input
                  value={bannerForm.link}
                  onChange={(e) => setBannerForm((f) => ({ ...f, link: e.target.value }))}
                  placeholder="/ 或 https://..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs font-medium text-gray-700">启用该轮播图</div>
                  <div className="text-xs text-gray-400 mt-0.5">关闭后该轮播图将不在前台显示</div>
                </div>
                <button
                  onClick={() => setBannerForm((f) => ({ ...f, enabled: !f.enabled }))}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${bannerForm.enabled ? "bg-orange-500" : "bg-gray-200"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${bannerForm.enabled ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setEditBanner(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
              <button
                onClick={saveBanner}
                disabled={!bannerForm.title.trim() || !bannerForm.image.trim()}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer disabled:opacity-50"
              >保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Tab: Agreement ----
function AgreementTab() {
  const [activeDoc, setActiveDoc] = useState<"register" | "privacy">("register");
  const [registerAgreement, setRegisterAgreement] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registerId, setRegisterId] = useState<string | null>(null);
  const [privacyId, setPrivacyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 加载协议内容
  useEffect(() => {
    async function loadAgreements() {
      try {
        const pages = await getSinglePages();
        const registerPage = pages.find(p => p.slug === 'register-agreement');
        const privacyPage = pages.find(p => p.slug === 'privacy');
        
        if (registerPage) {
          setRegisterAgreement(registerPage.content || "");
          setRegisterId(registerPage.id);
        } else {
          setRegisterAgreement("一、服务条款\n本协议是用户与商服汇之间关于使用商服汇服务所订立的协议。\n\n二、用户注册\n用户注册时须提供真实、准确、完整的个人资料，并及时更新。\n\n三、用户行为规范\n用户不得发布虚假信息、违法内容或侵犯他人权益的内容。\n\n四、免责声明\n商服汇对用户发布的信息内容不承担直接责任，但将积极配合相关部门处理违规内容。\n\n五、协议修改\n商服汇有权随时修改本协议，修改后的协议将在平台公告后生效。");
        }
        
        if (privacyPage) {
          setPrivacyPolicy(privacyPage.content || "");
          setPrivacyId(privacyPage.id);
        } else {
          setPrivacyPolicy("一、信息收集\n我们收集您提供的注册信息（昵称、手机号、邮箱）以及您发布的服务信息。\n\n二、信息使用\n收集的信息用于提供、维护和改善我们的服务，以及向您发送服务通知。\n\n三、信息保护\n我们采用行业标准的安全措施保护您的个人信息，防止未经授权的访问。\n\n四、信息共享\n未经您的同意，我们不会将您的个人信息出售或出租给第三方。\n\n五、Cookie使用\n我们使用Cookie来改善用户体验，您可以通过浏览器设置拒绝Cookie。\n\n六、联系我们\n如您对隐私政策有任何疑问，请通过官方渠道联系我们。");
        }
      } catch (err) {
        console.error("加载协议失败:", err);
        toast("加载协议失败");
      } finally {
        setLoading(false);
      }
    }
    loadAgreements();
  }, []);

  // 保存协议
  const handleSave = async () => {
    setSaving(true);
    try {
      // 保存注册协议
      if (registerId) {
        await updateSinglePage(registerId, { content: registerAgreement });
      }
      // 保存隐私政策
      if (privacyId) {
        await updateSinglePage(privacyId, { content: privacyPolicy });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (err) {
      console.error("保存协议失败:", err);
      toast("保存失败");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-gray-400">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
        {([["register", "注册协议"], ["privacy", "隐私政策"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveDoc(key)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${activeDoc === key ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeDoc === "register" ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">注册协议内容</label>
          <textarea
            value={registerAgreement}
            onChange={(e) => setRegisterAgreement(e.target.value)}
            rows={16}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none font-mono leading-relaxed"
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">隐私政策内容</label>
          <textarea
            value={privacyPolicy}
            onChange={(e) => setPrivacyPolicy(e.target.value)}
            rows={16}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none font-mono leading-relaxed"
          />
        </div>
      )}
      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />
    </div>
  );
}

// ---- Tab: Features ----
function FeaturesTab() {
  const [requireReview, setRequireReview] = useState(false);
  const [allowRegister, setAllowRegister] = useState(true);
  const [allowReport, setAllowReport] = useState(true);
  const [allowFavorite, setAllowFavorite] = useState(true);
  const [allowHistory, setAllowHistory] = useState(true);
  const [showViewCount, setShowViewCount] = useState(true);
  const [enableVip, setEnableVip] = useState(true);
  const [enableTopping, setEnableTopping] = useState(false);
  const [hideCompanyName, setHideCompanyName] = useState(false);
  const [freePostLimit, setFreePostLimit] = useState(10);
  const [postChargePerExtra, setPostChargePerExtra] = useState("1.00");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // 加载设置
  useEffect(() => {
    getSiteSettings().then((settings) => {
      setFreePostLimit(settings.free_post_limit ?? 10);
      setPostChargePerExtra(settings.post_charge_per_extra ?? "1.00");
      setEnableVip(settings.enable_vip ?? true);
      setEnableTopping(settings.enable_topping ?? false);
      setHideCompanyName(settings.hide_company_name ?? false);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSiteSettings({
        free_post_limit: freePostLimit,
        post_charge_per_extra: postChargePerExtra,
        enable_vip: enableVip,
        enable_topping: enableTopping,
        hide_company_name: hideCompanyName,
      } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (err) {
      toast("保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <ToggleSwitch value={requireReview} onChange={setRequireReview} label="信息发布审核" desc="开启后，用户发布的信息需要管理员审核后才能展示" />
      <ToggleSwitch value={allowRegister} onChange={setAllowRegister} label="开放用户注册" desc="关闭后，新用户将无法注册账号" />
      <ToggleSwitch value={allowReport} onChange={setAllowReport} label="允许举报功能" desc="用户可以举报违规信息" />
      <ToggleSwitch value={allowFavorite} onChange={setAllowFavorite} label="允许收藏功能" desc="用户可以收藏感兴趣的服务信息" />
      <ToggleSwitch value={allowHistory} onChange={setAllowHistory} label="浏览记录功能" desc="记录用户的浏览历史" />
      <ToggleSwitch value={showViewCount} onChange={setShowViewCount} label="显示浏览次数" desc="在信息列表和详情页显示浏览次数" />
      <ToggleSwitch value={enableVip} onChange={setEnableVip} label="开启VIP功能" desc="关闭后，前台将隐藏VIP入口，用户无法购买VIP套餐" />
      <ToggleSwitch value={enableTopping} onChange={setEnableTopping} label="开启置顶功能" desc="关闭后，会员中心的置顶按钮将被隐藏" />
      <ToggleSwitch value={hideCompanyName} onChange={setHideCompanyName} label="隐藏公司名称" desc="开启后，信息详情页中将不显示公司名称字段" />

      {/* Free post limit */}
      <div className="flex items-center justify-between py-3.5 border-b border-gray-50">
        <div>
          <div className="text-sm font-medium text-gray-800">普通用户免费发帖数</div>
          <div className="text-xs text-gray-400 mt-0.5">普通用户（非VIP）每月可免费发布的帖子数量</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setFreePostLimit((v) => Math.max(0, v - 1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 cursor-pointer"
          >
            <i className="ri-subtract-line text-sm" />
          </button>
          <input
            type="number"
            min={0}
            max={999}
            value={freePostLimit}
            onChange={(e) => setFreePostLimit(Math.max(0, Math.min(999, Number(e.target.value))))}
            className="w-14 text-center px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
          />
          <button
            onClick={() => setFreePostLimit((v) => Math.min(999, v + 1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 cursor-pointer"
          >
            <i className="ri-add-line text-sm" />
          </button>
          <span className="text-sm text-gray-500">条/月</span>
        </div>
      </div>

      {/* Post charge per extra */}
      <div className="flex items-center justify-between py-3.5 border-b border-gray-50">
        <div>
          <div className="text-sm font-medium text-gray-800">超出免费额度后收费</div>
          <div className="text-xs text-gray-400 mt-0.5">普通用户超出免费发帖数后，每条收费金额（VIP会员不受限制）</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-gray-500">¥</span>
          <input
            type="number"
            min={0}
            step={0.01}
            value={postChargePerExtra}
            onChange={(e) => setPostChargePerExtra(e.target.value)}
            className="w-20 text-center px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
          />
          <span className="text-sm text-gray-500">元/条</span>
        </div>
      </div>

      <div className="pt-3">
        <SaveBar onSave={handleSave} saved={saved} disabled={saving} />
      </div>
    </div>
  );
}

// ---- Tab: VIP ----
interface VipPlan {
  id: string;
  name: string;
  duration: number;
  durationUnit: "day" | "month" | "year";
  price: number;
  originalPrice: number;
  features: string[];
  isRecommended: boolean;
  enabled: boolean;
}

interface PinPlan {
  id: string;
  name: string;
  duration: number;
  durationUnit: "hour" | "day" | "week";
  price: number;
  enabled: boolean;
}

const defaultVipPlans: VipPlan[] = [
  { id: "vp1", name: "月度会员", duration: 1, durationUnit: "month", price: 29.9, originalPrice: 39.9, features: ["发布信息不限次", "信息优先展示", "专属VIP标识"], isRecommended: false, enabled: true },
  { id: "vp2", name: "季度会员", duration: 3, durationUnit: "month", price: 79.9, originalPrice: 119.7, features: ["发布信息不限次", "信息优先展示", "专属VIP标识", "免费置顶次数x2"], isRecommended: true, enabled: true },
  { id: "vp3", name: "年度会员", duration: 1, durationUnit: "year", price: 259.9, originalPrice: 478.8, features: ["发布信息不限次", "信息优先展示", "专属VIP标识", "免费置顶次数x10", "专属客服支持"], isRecommended: false, enabled: true },
];

const defaultPinPlans: PinPlan[] = [
  { id: "pp1", name: "24小时置顶", duration: 24, durationUnit: "hour", price: 5, enabled: true },
  { id: "pp2", name: "3天置顶", duration: 3, durationUnit: "day", price: 12, enabled: true },
  { id: "pp3", name: "7天置顶", duration: 7, durationUnit: "day", price: 25, enabled: true },
  { id: "pp4", name: "30天置顶", duration: 30, durationUnit: "day", price: 88, enabled: true },
];

const durationUnitLabel: Record<string, string> = { hour: "小时", day: "天", week: "周", month: "月", year: "年" };

function VipTab() {
  const [vipPlans, setVipPlans] = useState<VipPlan[]>([]);
  const [pinPlans, setPinPlans] = useState<PinPlan[]>([]);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editVip, setEditVip] = useState<VipPlan | null>(null);
  const [editPin, setEditPin] = useState<PinPlan | null>(null);
  const [vipForm, setVipForm] = useState<VipPlan>(defaultVipPlans[0]);
  const [pinForm, setPinForm] = useState<PinPlan>(defaultPinPlans[0]);
  const [featureInput, setFeatureInput] = useState("");
  // 记录从后端加载的原始ID列表，用于检测删除
  const originalIdsRef = useRef<string[]>([]);
  const originalPinIdsRef = useRef<string[]>([]);

  // 加载VIP套餐数据
  useEffect(() => {
    async function loadVipPlans() {
      try {
        const plans = await getVipPlans();
        if (plans && Array.isArray(plans) && plans.length > 0) {
          const formatted = plans.map((p: any) => ({
            id: p.id || `vp-${Date.now()}`,
            name: p.name || "",
            duration: p.duration_months || p.duration || 1,
            durationUnit: p.duration_unit || "month",
            price: p.amount || p.price || 0,
            originalPrice: p.original_price || p.originalPrice || 0,
            features: p.benefits || p.features || [],
            isRecommended: p.is_recommended || p.isRecommended || false,
            enabled: p.status === 'active' || p.enabled !== false,
          }));
          setVipPlans(formatted);
          originalIdsRef.current = formatted.map((p) => p.id);
        } else {
          setVipPlans([]);
          originalIdsRef.current = [];
        }
      } catch (e) {
        console.error("加载VIP套餐失败:", e);
      } finally {
        setLoading(false);
      }
    }
    loadVipPlans();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // 检测被删除的VIP套餐，发送DELETE请求
      const currentIds = new Set(vipPlans.map((p) => p.id));
      const deletedIds = originalIdsRef.current.filter((id) => !currentIds.has(id));
      for (const deletedId of deletedIds) {
        try { await deleteVipPlan(deletedId); } catch { /* 删除不存在的套餐忽略 */ }
      }

      // 转换VIP数据格式以适配后端
      const plansToSave = vipPlans.map((p) => ({
        id: p.id,
        name: p.name,
        duration_months: p.durationUnit === 'year' ? p.duration * 12 : p.durationUnit === 'day' ? Math.ceil(p.duration / 30) : p.duration,
        duration_unit: p.durationUnit,
        amount: p.price,
        original_price: p.originalPrice,
        benefits: p.features,
        is_recommended: p.isRecommended,
        status: p.enabled ? 'active' : 'inactive',
      }));
      await saveVipPlans(plansToSave);

      // 更新原始ID引用
      originalIdsRef.current = vipPlans.map((p) => p.id);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast("保存成功");
    } catch (e) {
      console.error("保存VIP套餐失败:", e);
      toast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAddVip = () => {
    const newPlan: VipPlan = { id: "vp-" + Date.now(), name: "", duration: 1, durationUnit: "month", price: 0, originalPrice: 0, features: [], isRecommended: false, enabled: true };
    setVipForm(newPlan);
    setFeatureInput("");
    setEditVip(newPlan);
  };

  const openEditVip = (plan: VipPlan) => {
    setVipForm({ ...plan, features: [...plan.features] });
    setFeatureInput("");
    setEditVip(plan);
  };

  const saveVip = () => {
    if (!vipForm.name.trim() || vipForm.price <= 0) return;
    setVipPlans((prev) => {
      const idx = prev.findIndex((p) => p.id === vipForm.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = vipForm; return next; }
      return [...prev, vipForm];
    });
    setEditVip(null);
  };

  const deleteVip = (id: string) => setVipPlans((prev) => prev.filter((p) => p.id !== id));
  const toggleVip = (id: string) => setVipPlans((prev) => prev.map((p) => p.id === id ? { ...p, enabled: !p.enabled } : p));
  const setRecommended = (id: string) => setVipPlans((prev) => prev.map((p) => ({ ...p, isRecommended: p.id === id })));

  const addFeature = () => {
    const f = featureInput.trim();
    if (f && !vipForm.features.includes(f)) {
      setVipForm((prev) => ({ ...prev, features: [...prev.features, f] }));
      setFeatureInput("");
    }
  };

  const removeFeature = (f: string) => setVipForm((prev) => ({ ...prev, features: prev.features.filter((x) => x !== f) }));

  const openAddPin = () => {
    const newPlan: PinPlan = { id: "pp-" + Date.now(), name: "", duration: 1, durationUnit: "day", price: 0, enabled: true };
    setPinForm(newPlan);
    setEditPin(newPlan);
  };

  const openEditPin = (plan: PinPlan) => {
    setPinForm({ ...plan });
    setEditPin(plan);
  };

  const savePin = () => {
    if (!pinForm.name.trim() || pinForm.price <= 0) return;
    setPinPlans((prev) => {
      const idx = prev.findIndex((p) => p.id === pinForm.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = pinForm; return next; }
      return [...prev, pinForm];
    });
    setEditPin(null);
  };

  const deletePin = (id: string) => setPinPlans((prev) => prev.filter((p) => p.id !== id));
  const togglePin = (id: string) => setPinPlans((prev) => prev.map((p) => p.id === id ? { ...p, enabled: !p.enabled } : p));

  return (
    <div className="space-y-7">
      {/* VIP Plans */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">VIP套餐管理</h3>
            <p className="text-xs text-gray-400 mt-0.5">设置用户可购买的VIP会员套餐和价格</p>
          </div>
          <button onClick={openAddVip} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" />新增套餐
          </button>
        </div>
        <div className="space-y-2">
          {vipPlans.map((plan) => (
            <div key={plan.id} className={`flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100 ${!plan.enabled ? "opacity-50" : ""}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-800">{plan.name}</span>
                  {plan.isRecommended && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded-full">推荐</span>}
                  {!plan.enabled && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-400 text-xs rounded-full">已禁用</span>}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-base font-bold text-orange-500">￥{plan.price}</span>
                  {plan.originalPrice > plan.price && <span className="text-xs text-gray-400 line-through">￥{plan.originalPrice}</span>}
                  <span className="text-xs text-gray-500">{plan.duration}{durationUnitLabel[plan.durationUnit]}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {plan.features.map((f) => (
                    <span key={f} className="text-xs text-gray-500 bg-white border border-gray-100 px-2 py-0.5 rounded-full">{f}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setRecommended(plan.id)}
                  title="设为推荐"
                  className={`px-2.5 py-1 text-xs border rounded-lg cursor-pointer transition-colors ${plan.isRecommended ? "border-orange-300 text-orange-500 bg-orange-50" : "border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-500"}`}
                >推荐</button>
                <button onClick={() => toggleVip(plan.id)} className={`relative w-8 h-5 rounded-full transition-colors cursor-pointer ${plan.enabled ? "bg-orange-500" : "bg-gray-200"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${plan.enabled ? "translate-x-3" : "translate-x-0"}`} />
                </button>
                <button onClick={() => openEditVip(plan)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer">编辑</button>
                <button onClick={() => deleteVip(plan.id)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer">删除</button>
              </div>
            </div>
          ))}
          {vipPlans.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">暂无VIP套餐，点击新增</div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Pin Plans */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">置顶套餐管理</h3>
            <p className="text-xs text-gray-400 mt-0.5">设置置顶时长和对应价格，用户可以按需购买</p>
          </div>
          <button onClick={openAddPin} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" />新增置顶套餐
          </button>
        </div>
        <div className="space-y-2">
          {pinPlans.map((plan) => (
            <div key={plan.id} className={`flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100 ${!plan.enabled ? "opacity-50" : ""}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-800">{plan.name}</span>
                  <span className="text-xs text-gray-500">{plan.duration}{durationUnitLabel[plan.durationUnit]}</span>
                  {!plan.enabled && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-400 text-xs rounded-full">已禁用</span>}
                </div>
                <div className="mt-1">
                  <span className="text-base font-bold text-orange-500">￥{plan.price}</span>
                  <span className="text-xs text-gray-400 ml-1">/次</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => togglePin(plan.id)} className={`relative w-8 h-5 rounded-full transition-colors cursor-pointer ${plan.enabled ? "bg-orange-500" : "bg-gray-200"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${plan.enabled ? "translate-x-3" : "translate-x-0"}`} />
                </button>
                <button onClick={() => openEditPin(plan)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer">编辑</button>
                <button onClick={() => deletePin(plan.id)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer">删除</button>
              </div>
            </div>
          ))}
          {pinPlans.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">暂无置顶套餐，点击新增</div>
          )}
        </div>
      </div>

      <SaveBar onSave={handleSave} saved={saved} disabled={saving} />

      {/* Edit VIP Modal */}
      {editVip && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditVip(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">{vipPlans.find(p => p.id === vipForm.id) ? "编辑VIP套餐" : "新增VIP套餐"}</h3>
              <button onClick={() => setEditVip(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">套餐名称 <span className="text-red-400">*</span></label>
                  <input value={vipForm.name} onChange={(e) => setVipForm(f => ({ ...f, name: e.target.value }))} placeholder="如：月度会员" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">套餐时长</label>
                  <div className="flex gap-2">
                    <input type="number" min={1} value={vipForm.duration} onChange={(e) => setVipForm(f => ({ ...f, duration: Number(e.target.value) }))} className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                    <select value={vipForm.durationUnit} onChange={(e) => setVipForm(f => ({ ...f, durationUnit: e.target.value as VipPlan["durationUnit"] }))} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 cursor-pointer">
                      <option value="day">天</option>
                      <option value="month">月</option>
                      <option value="year">年</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">实价（￥） <span className="text-red-400">*</span></label>
                  <input type="number" min={0} step={0.01} value={vipForm.price} onChange={(e) => setVipForm(f => ({ ...f, price: Number(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">划线价（￥）</label>
                  <input type="number" min={0} step={0.01} value={vipForm.originalPrice} onChange={(e) => setVipForm(f => ({ ...f, originalPrice: Number(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">套餐权益</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {vipForm.features.map((f) => (
                    <span key={f} className="flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-600 text-xs rounded-full">
                      {f}
                      <button onClick={() => removeFeature(f)} className="w-3 h-3 flex items-center justify-center hover:text-red-500 cursor-pointer"><i className="ri-close-line text-xs" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addFeature()} placeholder="输入权益描述，回车添加" className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                  <button onClick={addFeature} className="px-3 py-2 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap">添加</button>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs font-medium text-gray-700">设为推荐套餐</div>
                  <div className="text-xs text-gray-400 mt-0.5">展示时会高亮显示推荐标识</div>
                </div>
                <button onClick={() => setVipForm(f => ({ ...f, isRecommended: !f.isRecommended }))} className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${vipForm.isRecommended ? "bg-orange-500" : "bg-gray-200"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${vipForm.isRecommended ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setEditVip(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
              <button onClick={saveVip} disabled={!vipForm.name.trim() || vipForm.price <= 0} className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer disabled:opacity-50">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pin Modal */}
      {editPin && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditPin(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">{pinPlans.find(p => p.id === pinForm.id) ? "编辑置顶套餐" : "新增置顶套餐"}</h3>
              <button onClick={() => setEditPin(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">套餐名称 <span className="text-red-400">*</span></label>
                <input value={pinForm.name} onChange={(e) => setPinForm(f => ({ ...f, name: e.target.value }))} placeholder="如：7天置顶" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">置顶时长</label>
                  <div className="flex gap-2">
                    <input type="number" min={1} value={pinForm.duration} onChange={(e) => setPinForm(f => ({ ...f, duration: Number(e.target.value) }))} className="w-16 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                    <select value={pinForm.durationUnit} onChange={(e) => setPinForm(f => ({ ...f, durationUnit: e.target.value as PinPlan["durationUnit"] }))} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 cursor-pointer">
                      <option value="hour">小时</option>
                      <option value="day">天</option>
                      <option value="week">周</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">价格（￥） <span className="text-red-400">*</span></label>
                  <input type="number" min={0} step={0.01} value={pinForm.price} onChange={(e) => setPinForm(f => ({ ...f, price: Number(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setEditPin(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
              <button onClick={savePin} disabled={!pinForm.name.trim() || pinForm.price <= 0} className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer disabled:opacity-50">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Tab: Register ----
type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "image" | "file" | "date" | "number";

const fieldTypeLabels: Record<FieldType, string> = {
  text: "单行文本",
  textarea: "多行文本",
  select: "下拉选择",
  radio: "单选",
  checkbox: "多选",
  image: "图片上传",
  file: "文件上传",
  date: "日期",
  number: "手机号",
};

const fieldTypeIcons: Record<FieldType, string> = {
  text: "ri-text",
  textarea: "ri-align-left",
  select: "ri-arrow-down-s-line",
  radio: "ri-radio-button-line",
  checkbox: "ri-checkbox-line",
  image: "ri-image-line",
  file: "ri-file-upload-line",
  date: "ri-calendar-line",
  number: "ri-hashtag",
};

interface RegisterField {
  id: string;
  field_name: string;
  field_label: string;
  field_type: FieldType;
  required: boolean;
  enabled: boolean;
  placeholder: string;
  options: string[];
  hint: string;
  sort_order?: number;
}

function RegisterTab() {
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsForm, setSmsForm] = useState({
    accessKeyId: "",
    accessKeySecret: "",
    signName: "",
    templateCode: "",
    region: "cn-hangzhou",
  });
  const [fields, setFields] = useState<RegisterField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [editField, setEditField] = useState<RegisterField | null>(null);
  const [fieldForm, setFieldForm] = useState<RegisterField | null>(null);
  const [optionInput, setOptionInput] = useState("");
  const [saved, setSaved] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string>("");
  const [smsSaved, setSmsSaved] = useState(false);
  const [smsLoading, setSmsLoading] = useState(true);
  const [smsToggling, setSmsToggling] = useState(false);
  const [smsToggled, setSmsToggled] = useState(false);

  // 加载短信配置
  const loadSmsConfig = useCallback(async () => {
    try {
      setSmsLoading(true);
      const config = await getSmsConfig();
      setSmsEnabled(config.enabled);
      setSmsForm({
        accessKeyId: config.access_key_id || "",
        accessKeySecret: config.access_key_secret || "",
        signName: config.sign_name || "",
        templateCode: config.template_code || "",
        region: config.region || "cn-hangzhou",
      });
    } catch (error: any) {
      console.error("加载短信配置失败:", error);
    } finally {
      setSmsLoading(false);
    }
  }, []);

  // 加载注册字段
  const loadFields = useCallback(async () => {
    try {
      setFieldsLoading(true);
      const data = await getRegisterFields();
      // 转换为UI使用的格式
      const uiFields = data.map((f: ApiRegisterField) => ({
        id: f.id,
        field_name: f.field_name,
        field_label: f.field_label,
        field_type: f.field_type as FieldType,
        required: f.is_required,
        enabled: f.is_enabled,
        placeholder: f.placeholder || "",
        options: f.options || [],
        hint: f.hint || "",
        sort_order: f.sort_order,
      }));
      setFields(uiFields);
    } catch (error: any) {
      toast(error.message || "加载注册字段失败", 'error');
    } finally {
      setFieldsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  useEffect(() => {
    loadSmsConfig();
  }, [loadSmsConfig]);

  // 自动保存函数（用于自定义字段）
  const autoSave = useCallback(async (currentFields: RegisterField[]) => {
    try {
      setSaving(true);
      // 转换为API格式
      const apiFields = currentFields.map((f, idx) => ({
        field_name: f.field_name || `field_${idx + 1}`,
        field_label: f.field_label,
        field_type: f.field_type,
        placeholder: f.placeholder,
        hint: f.hint,
        options: f.options,
        is_required: f.required,
        is_enabled: f.enabled,
      }));
      await saveRegisterFields(apiFields);
      setLastSaved(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error: any) {
      toast(error.message || "保存失败", 'error');
    } finally {
      setSaving(false);
    }
  }, []);

  // 切换必填状态
  const toggleRequired = useCallback((id: string) => {
    setFields(prev => {
      const next = prev.map(f => f.id === id ? { ...f, required: !f.required } : f);
      autoSave(next);
      return next;
    });
  }, [autoSave]);

  // 切换启用状态
  const toggleField = useCallback((id: string) => {
    setFields(prev => {
      const next = prev.map(f => f.id === id ? { ...f, enabled: !f.enabled } : f);
      autoSave(next);
      return next;
    });
  }, [autoSave]);

  // 删除字段
  const deleteField = useCallback((id: string) => {
    setFields(prev => {
      const next = prev.filter(f => f.id !== id);
      autoSave(next);
      return next;
    });
  }, [autoSave]);

  // 移动字段
  const moveField = useCallback((idx: number, dir: -1 | 1) => {
    setFields(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      autoSave(next);
      return next;
    });
  }, [autoSave]);

  // 保存字段（新增或编辑）
  const saveField = useCallback(() => {
    if (!fieldForm || !fieldForm.field_label.trim()) return;
    setFields(prev => {
      const idx = prev.findIndex((f) => f.id === fieldForm.id);
      let next: RegisterField[];
      if (idx >= 0) {
        next = [...prev];
        next[idx] = fieldForm;
      } else {
        next = [...prev, fieldForm];
      }
      autoSave(next);
      return next;
    });
    setEditField(null);
    setFieldForm(null);
  }, [fieldForm, autoSave]);

  // 保存短信配置
  const handleSaveSms = useCallback(async () => {
    try {
      setSaving(true);
      await saveSmsConfig({
        enabled: smsEnabled,
        access_key_id: smsForm.accessKeyId,
        access_key_secret: smsForm.accessKeySecret,
        sign_name: smsForm.signName,
        template_code: smsForm.templateCode,
        region: smsForm.region,
      });
      setSmsSaved(true);
      setTimeout(() => setSmsSaved(false), 2000);
      toast('短信配置已保存', 'success');
    } catch (error: any) {
      toast(error.message || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  }, [smsEnabled, smsForm]);

  // 切换短信开关（点击即保存）
  const handleToggleSms = useCallback(async () => {
    const newEnabled = !smsEnabled;
    console.log('切换短信开关:', { current: smsEnabled, new: newEnabled });
    setSmsToggling(true);
    try {
      const configData = {
        enabled: newEnabled,
        access_key_id: smsForm.accessKeyId,
        access_key_secret: smsForm.accessKeySecret,
        sign_name: smsForm.signName,
        template_code: smsForm.templateCode,
        region: smsForm.region,
      };
      console.log('保存短信配置:', configData);
      
      const result = await saveSmsConfig(configData);
      console.log('保存短信配置响应:', result);
      
      console.log('保存成功，更新状态:', newEnabled);
      setSmsEnabled(newEnabled);
      setSmsToggled(true);
      setTimeout(() => setSmsToggled(false), 2000);
      toast(newEnabled ? '短信验证已开启' : '短信验证已关闭', 'success');
    } catch (error: any) {
      console.error('保存短信配置失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response,
        status: error.status
      });
      toast(error.message || '保存失败: ' + (error.message || '未知错误'), 'error');
      // 重新加载配置以确保状态一致
      loadSmsConfig();
    } finally {
      setSmsToggling(false);
    }
  }, [smsEnabled, smsForm, loadSmsConfig]);

  const openAddField = () => {
    const newField: RegisterField = { 
      id: "new-" + Date.now(), 
      field_name: "", 
      field_label: "", 
      field_type: "text", 
      required: false, 
      enabled: true, 
      placeholder: "", 
      options: [], 
      hint: "" 
    };
    setFieldForm(newField);
    setOptionInput("");
    setEditField(newField);
  };

  const openEditField = (f: RegisterField) => {
    setFieldForm({ ...f, options: [...f.options] });
    setOptionInput("");
    setEditField(f);
  };

  const addOption = () => {
    if (!fieldForm) return;
    const opt = optionInput.trim();
    if (opt && !fieldForm.options.includes(opt)) {
      setFieldForm((f) => f ? { ...f, options: [...f.options, opt] } : f);
      setOptionInput("");
    }
  };

  const removeOption = (opt: string) => {
    if (!fieldForm) return;
    setFieldForm((f) => f ? { ...f, options: f.options.filter((o) => o !== opt) } : f);
  };

  const needsOptions = fieldForm && ["select", "radio", "checkbox"].includes(fieldForm.field_type);

  return (
    <div className="space-y-7">
      {/* SMS Config */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">验证码短信配置</h3>
            <p className="text-xs text-gray-400 mt-0.5">基于阿里云短信服务，用于注册时发送手机验证码 | 当前状态: {smsEnabled ? '开启' : '关闭'}</p>
          </div>
          <div className="flex items-center gap-3">
            {smsToggling && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-loader-2-line animate-spin" />
                保存中...
              </span>
            )}
            {smsToggled && !smsToggling && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line" />
                已保存
              </span>
            )}
            <div
              onClick={handleToggleSms}
              className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${smsEnabled ? "bg-orange-500" : "bg-gray-300"} ${smsToggling ? "opacity-50" : ""}`}
            >
              <span 
                className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${smsEnabled ? "right-1" : "left-1"}`} 
              />
            </div>
          </div>
        </div>

        <div className={`space-y-4 transition-opacity ${smsEnabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
          <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-2.5">
            <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-500 text-sm" />
            </div>
            <p className="text-xs text-amber-700 leading-relaxed">
              请前往
              <a href="https://www.aliyun.com/product/sms" target="_blank" rel="nofollow" className="font-medium underline mx-1">阿里云短信服务</a>
              开通服务并获取 AccessKey，在短信控制台创建签名和模板后填入下方配置。
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1.5">AccessKey ID <span className="text-red-400">*</span></label>
              <input
                value={smsForm.accessKeyId}
                onChange={(e) => setSmsForm((f) => ({ ...f, accessKeyId: e.target.value }))}
                placeholder="LTAI5t..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1.5">AccessKey Secret <span className="text-red-400">*</span></label>
              <div className="relative">
                <input
                  type={showSecret ? "text" : "password"}
                  value={smsForm.accessKeySecret}
                  onChange={(e) => setSmsForm((f) => ({ ...f, accessKeySecret: e.target.value }))}
                  placeholder="请输入 AccessKey Secret"
                  className="w-full px-3 py-2 pr-9 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono"
                />
                <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className={`${showSecret ? "ri-eye-line" : "ri-eye-off-line"} text-sm`} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1.5">短信签名 <span className="text-red-400">*</span></label>
              <input
                value={smsForm.signName}
                onChange={(e) => setSmsForm((f) => ({ ...f, signName: e.target.value }))}
                placeholder="如：商服汇"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
              <p className="text-xs text-gray-400 mt-1">需在阿里云控制台审核通过</p>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1.5">模板 Code <span className="text-red-400">*</span></label>
              <input
                value={smsForm.templateCode}
                onChange={(e) => setSmsForm((f) => ({ ...f, templateCode: e.target.value }))}
                placeholder="SMS_XXXXXXXXX"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">模板内容建议：您的验证码为{'${code}'}，5分钟内有效</p>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1.5">地域节点</label>
              <select
                value={smsForm.region}
                onChange={(e) => setSmsForm((f) => ({ ...f, region: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 cursor-pointer"
              >
                <option value="cn-hangzhou">华东（杭州）</option>
                <option value="cn-beijing">华北（北京）</option>
                <option value="cn-shanghai">华东（上海）</option>
                <option value="cn-shenzhen">华南（深圳）</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-shield-check-line text-gray-400 text-sm" />
            </div>
            <p className="text-xs text-gray-500 flex-1">AccessKey Secret 将加密存储，仅用于服务端调用，不会暴露到前端页面</p>
          </div>

          {/* 保存短信配置按钮 */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSaveSms}
              className="px-6 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              保存配置
            </button>
            {smsSaved && (
              <span className="text-sm text-green-600 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line" />
                保存成功
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Custom Fields */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">自定义注册字段</h3>
            <p className="text-xs text-gray-400 mt-0.5">配置注册页面需要用户填写的额外信息，支持多种字段类型</p>
          </div>
          <button
            onClick={openAddField}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />新增字段
          </button>
        </div>

        <div className="space-y-2">
          {fieldsLoading ? (
            <div className="py-8 text-center text-sm text-gray-400">加载中...</div>
          ) : fields.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">暂无自定义字段，点击新增</div>
          ) : (
            fields.map((field, idx) => (
              <div key={field.id} className={`flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-100 ${!field.enabled ? "opacity-50" : ""}`}>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
                    <i className="ri-arrow-up-s-line text-xs" />
                  </button>
                  <button onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1} className="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
                    <i className="ri-arrow-down-s-line text-xs" />
                  </button>
                </div>
                <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-100 shrink-0">
                  <i className={`${fieldTypeIcons[field.field_type]} text-orange-400 text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800">{field.field_label}</span>
                    <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{fieldTypeLabels[field.field_type]}</span>
                    {field.required && <span className="text-xs bg-red-50 text-red-500 px-1.5 py-0.5 rounded">必填</span>}
                    {!field.enabled && <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">已隐藏</span>}
                  </div>
                  {field.placeholder && <p className="text-xs text-gray-400 mt-0.5 truncate">{field.placeholder}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleRequired(field.id)}
                    title={field.required ? "取消必填" : "设为必填"}
                    className={`px-2 py-1 text-xs border rounded-lg cursor-pointer transition-colors ${field.required ? "border-red-200 text-red-500 bg-red-50" : "border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-400"}`}
                  >必填</button>
                  <button onClick={() => toggleField(field.id)} className={`relative w-8 h-5 rounded-full transition-colors cursor-pointer ${field.enabled ? "bg-orange-500" : "bg-gray-200"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${field.enabled ? "translate-x-3" : "translate-x-0"}`} />
                  </button>
                  <button onClick={() => openEditField(field)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer">编辑</button>
                  <button onClick={() => deleteField(field.id)} className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer">删除</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 自动保存提示（仅在保存时显示） */}
      {saving && (
        <div className="flex items-center gap-2 pt-2">
          <span className="text-sm text-gray-400 flex items-center gap-1.5">
            <i className="ri-loader-2-line animate-spin" />
            保存中...
          </span>
        </div>
      )}
      {lastSaved && !saving && (
        <div className="flex items-center gap-2 pt-2">
          <span className="text-sm text-green-600 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line" />
            已保存 {lastSaved}
          </span>
          <span className="text-xs text-gray-400">（操作将自动保存）</span>
        </div>
      )}

      {/* Edit Field Modal */}
      {editField && fieldForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setEditField(null); setFieldForm(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">{fields.find(f => f.id === fieldForm.id) ? "编辑字段" : "新增字段"}</h3>
              <button onClick={() => { setEditField(null); setFieldForm(null); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">字段名称 <span className="text-red-400">*</span></label>
                  <input
                    value={fieldForm.field_label}
                    onChange={(e) => setFieldForm((f) => f ? { ...f, field_label: e.target.value } : f)}
                    placeholder="如：公司名称"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">字段类型</label>
                  <select
                    value={fieldForm.field_type}
                    onChange={(e) => setFieldForm((f) => f ? { ...f, field_type: e.target.value as FieldType, options: [] } : f)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 cursor-pointer"
                  >
                    {(Object.keys(fieldTypeLabels) as FieldType[]).map((t) => (
                      <option key={t} value={t}>{fieldTypeLabels[t]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1.5">占位提示文字</label>
                <input
                  value={fieldForm.placeholder}
                  onChange={(e) => setFieldForm((f) => f ? { ...f, placeholder: e.target.value } : f)}
                  placeholder="用户未输入时显示的提示文字"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1.5">辅助说明</label>
                <input
                  value={fieldForm.hint}
                  onChange={(e) => setFieldForm((f) => f ? { ...f, hint: e.target.value } : f)}
                  placeholder="显示在字段下方的辅助说明"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                />
              </div>

              {needsOptions && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">选项列表</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {fieldForm.options.map((opt) => (
                      <span key={opt} className="flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-600 text-xs rounded-full">
                        {opt}
                        <button onClick={() => removeOption(opt)} className="w-3 h-3 flex items-center justify-center hover:text-red-500 cursor-pointer"><i className="ri-close-line text-xs" /></button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addOption()}
                      placeholder="输入选项，回车添加"
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                    />
                    <button onClick={addOption} className="px-3 py-2 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap">添加</button>
                  </div>
                </div>
              )}

              {(fieldForm.field_type === "image" || fieldForm.field_type === "file") && (
                <div className="p-3 bg-blue-50 rounded-xl flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-information-line text-blue-500 text-sm" />
                  </div>
                  <div className="text-xs text-blue-700 leading-relaxed">
                    {fieldForm.field_type === "image"
                      ? "图片上传字段：用户可上传图片文件（JPG/PNG/GIF），建议在辅助说明中注明大小限制和格式要求。"
                      : "文件上传字段：用户可上传任意格式文件，建议在辅助说明中注明支持的文件格式。"
                    }
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs font-medium text-gray-700">必填字段</div>
                  <div className="text-xs text-gray-400 mt-0.5">开启后用户必须填写此字段才能注册</div>
                </div>
                <button
                  onClick={() => setFieldForm((f) => f ? { ...f, required: !f.required } : f)}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${fieldForm.required ? "bg-orange-500" : "bg-gray-200"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${fieldForm.required ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => { setEditField(null); setFieldForm(null); }} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
              <button
                onClick={saveField}
                disabled={!fieldForm.field_label.trim()}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer disabled:opacity-50"
              >保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Tab: SSL ----
function SslTab() {
  const [enabled, setEnabled] = useState(false);
  const [certPath, setCertPath] = useState("/etc/nginx/ssl/fullchain.crt");
  const [keyPath, setKeyPath] = useState("/etc/nginx/ssl/private.key");
  const [domain, setDomain] = useState("www.yourdomain.com");
  const [showCertHelp, setShowCertHelp] = useState(false);
  const [saved, setSaved] = useState(false);
  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const nginxConfig = `server {
    listen 80;
    server_name ${domain};
    return 301 https://$server_name$request_uri;
}

server {
    listen 443 ssl;
    server_name ${domain};

    ssl_certificate     ${certPath};
    ssl_certificate_key ${keyPath};
    ssl_protocols       TLSv1.2 TLSv1.3;
    ssl_ciphers         HIGH:!aNULL:!MD5;
    ssl_prefer_server_ciphers on;

    location / {
        proxy_pass http://localhost:3000;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
        proxy_set_header X-Forwarded-Proto $scheme;
    }
}`;

  const [copied, setCopied] = useState(false);
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-6">
      {/* SSL Switch */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-800">启用 SSL 加密</div>
            <div className="text-xs text-gray-400 mt-0.5">开启后网站将使用 HTTPS 协议访问，提升安全性与搜索引擎权重</div>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative w-11 h-7 rounded-full transition-colors cursor-pointer shrink-0 ${enabled ? "bg-orange-500" : "bg-gray-200"}`}
          >
            <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-[18px]" : "translate-x-0"}`} />
          </button>
        </div>
      </div>

      {/* SSL Cert Paths */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            证书文件路径（CRT）
          </label>
          <input
            value={certPath}
            onChange={(e) => setCertPath(e.target.value)}
            placeholder="/etc/nginx/ssl/fullchain.crt"
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">通常是 fullchain.crt 或 .pem 文件，包含服务器证书 + 中间证书</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            私钥文件路径（KEY）
          </label>
          <input
            value={keyPath}
            onChange={(e) => setKeyPath(e.target.value)}
            placeholder="/etc/nginx/ssl/private.key"
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">私钥文件需妥善保管，切勿泄露</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            域名
          </label>
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="www.yourdomain.com"
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
          />
        </div>
      </div>

      {/* Help toggle */}
      <div>
        <button
          onClick={() => setShowCertHelp(!showCertHelp)}
          className="flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-600 cursor-pointer"
        >
          <i className={showCertHelp ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          如何获取和配置 SSL 证书？
        </button>
        {showCertHelp && (
          <div className="mt-3 p-4 bg-amber-50 rounded-xl border border-amber-100 space-y-3">
            <div>
              <div className="text-sm font-medium text-amber-800 mb-1">方式一：Let&apos;s Encrypt 免费证书（推荐）</div>
              <div className="text-xs text-amber-700 leading-relaxed space-y-1">
                <p>1. 安装 certbot：<span className="font-mono bg-white px-1.5 py-0.5 rounded text-amber-900">sudo apt install certbot python3-certbot-nginx</span></p>
                <p>2. 申请证书：<span className="font-mono bg-white px-1.5 py-0.5 rounded text-amber-900">sudo certbot --nginx -d {domain}</span></p>
                <p>3. 自动续期已配置，无需额外操作</p>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-amber-800 mb-1">方式二：阿里云/腾讯云 SSL 证书</div>
              <div className="text-xs text-amber-700 leading-relaxed">
                在阿里云或腾讯云控制台申请 SSL 证书，下载 Nginx 格式的证书文件（包含 .crt 和 .key），上传到服务器的指定目录即可。
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-amber-800 mb-1">方式三：自签名证书（仅限测试环境）</div>
              <div className="text-xs text-amber-700 leading-relaxed space-y-1">
                <p>1. 生成私钥：<span className="font-mono bg-white px-1.5 py-0.5 rounded text-amber-900">openssl genrsa -out private.key 2048</span></p>
                <p>2. 生成证书：<span className="font-mono bg-white px-1.5 py-0.5 rounded text-amber-900">openssl req -new -x509 -key private.key -out fullchain.crt -days 365</span></p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nginx Config Preview */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">nginx 配置参考</label>
          <div className="flex items-center gap-2">
            {copied && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line" />
                已复制
              </span>
            )}
            <button
              onClick={() => copyToClipboard(nginxConfig)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-copy-line" />
              复制配置
            </button>
          </div>
        </div>
        <div className="relative">
          <pre className="w-full p-4 text-xs font-mono bg-gray-900 text-gray-100 rounded-xl overflow-x-auto leading-relaxed">{nginxConfig}</pre>
          <div className="absolute top-2 right-2 px-2 py-1 bg-gray-800 text-gray-400 text-xs rounded-md">nginx.conf</div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          将上述配置添加到 /etc/nginx/sites-enabled/default 或对应的站点配置文件中，然后执行 <span className="font-mono bg-gray-100 px-1 rounded">sudo nginx -t && sudo systemctl reload nginx</span> 使配置生效。
        </p>
      </div>

      {/* Warning */}
      <div className="p-3 bg-red-50 rounded-xl border border-red-100 flex items-start gap-2.5">
        <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-error-warning-line text-red-500 text-sm" />
        </div>
        <div className="text-xs text-red-700 leading-relaxed">
          <strong>注意：</strong>SSL 配置仅保存路径信息，实际的证书文件部署需要在服务器端完成。请确保证书文件已上传到配置的路径，且 nginx 已正确加载。修改配置后请务必测试配置是否正确（nginx -t）再重载服务。
        </div>
      </div>

      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>("basic");

  const renderTab = () => {
    switch (activeTab) {
      case "basic": return <BasicTab />;
      case "logo": return <LogoTab />;
      case "keywords": return <KeywordsTab />;
      case "contact": return <ContactTab />;
      case "footer-menus": return <FooterMenusTab />;
      case "search": return <SearchTab />;
      case "banner": return <BannerTab />;
      case "agreement": return <AgreementTab />;
      case "features": return <FeaturesTab />;
      case "vip": return <VipTab />;
      case "register": return <RegisterTab />;
      case "ssl": return <SslTab />;
      default: return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-800">网站设置</h1>
          <p className="text-sm text-gray-500 mt-1">配置网站基本信息、外观和功能</p>
        </div>

        <div className="flex gap-6">
          {/* Left Tab Nav */}
          <div className="w-44 shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2.5 w-full px-4 py-3 text-sm transition-colors cursor-pointer border-b border-gray-50 last:border-0 ${
                    activeTab === tab.key
                      ? "bg-orange-50 text-orange-600 font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${tab.icon} text-sm`} />
                  </div>
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6 min-w-0">
            {renderTab()}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
